<template>
  <div class="bruker mt-md-0 mt-0">
    <div class="pt-md-2 pt-0 text-center">
      <div class="mb-5 text-left">
        <hl-H1>
          {{ translate('home.header title') }}
        </hl-H1>

        <p>
          Før du starter må du opprette en personlig brukerprofil med informasjon om deg som
          utleier. Deretter vil du få tilgang til å administrere dine leieforhold på en enkel, trygg
          og lønnsom måte.
        </p>

        <p class="mt-5 m-0">
          <strong>Dersom du er leietaker</strong> kan du opprette bruker
          <a :href="loginURL + '/signup/tenant'">
            her.
          </a>
        </p>

        <p class="m-0">
          <strong>Eksisterende brukere</strong> kan logge inn
          <a :href="loginURL">
            her.
          </a>
        </p>

        <p class="m-0">
          <strong>Obs!</strong> Merk at du må ha et norsk telefonnummer for å kunne bruke Husleie.no.
        </p>
      </div>

      <!-- Full name -->
      <div class="row mb-4">
        <div class="col">
          <hl-Input
            type="text"
            placeholder="Ulf Utleiersen"
            label="Fullt navn"
            v-model="signup.user.fullName"
            name="user.fullName"
            v-validate
            data-cy="full-name-component"
          />
        </div>
      </div>
      <!-- / Full name -->

      <div class="row row-cols-1 row-cols-md-2 gap-y-4">
          <!-- Email -->
          <div class="col">
            <hl-Input
              type="email"
              placeholder="ulf@utleiersen.no"
              label="E-post"
              v-model="signup.user.email"
              name="user.email"
              v-validate
              @blur="checkEmailInUse"
              data-cy="email-component"
            />
          </div>
          <!-- / Email -->


          <!-- Phone -->
          <div class="col">
            <hl-Phone
              type="text"
              placeholder="+47"
              label="Landkode"
              v-model="signup.user.phonePrefix"
              name="user.phonePrefix"
              disabled
              v-validate
            >
              <hl-Input
                type="tel"
                label="Mobilnummer"
                v-model="signup.user.cellPhone"
                :placeholder="phoneExample"
                name="user.cellPhone"
                v-validate
                data-cy="phone-component"
            />
            </hl-Phone>
          </div>
          <!-- / Phone -->
        </div>

      <div class="row row-cols-1 row-cols-md-2 gap-y-4 mt-4">
          <!-- Password -->
          <div class="col">
            <hl-Input
              v-model="signup.user.password"
              label="Passord (minst 8 tegn)"
              v-validate="'required|min:8'"
              name="password"
              type="password"
              ref="password"
              data-vv-as="Passord"
              data-vv-delay="1000"
              data-cy="password-component"
            />
          </div>
          <!-- / Password -->

          <!-- Repeat Password -->
          <div class="col">
            <hl-Input
              v-model="signup.user.passwordRepeat"
              label="Gjenta passord"
              v-validate="'required|confirmed:password'"
              name="password_confirmation"
              type="password"
              data-vv-as="Gjenta passord"
              data-vv-delay="1000"
              data-cy="repeat-password-component"
            />
          </div>
          <!-- / Repeat Password -->
        </div>

        <div class="row pt-4 mt-4 mb-5 equal-height">
          <div class="col">
            <hl-RadioButtonTapWithIcon
                iconClass="budicon-house-a"
                v-model="signup.landlord.type"
                radioValue="private"
            >
              <p data-cy="private-landlord-choice">Jeg leier ut som</p>
              <p class="font-weight-bold">privatperson</p>
            </hl-RadioButtonTapWithIcon>
          </div>

          <div class="col">
            <hl-RadioButtonTapWithIcon
                iconClass="budicon-business-area"
                v-model="signup.landlord.type"
                radioValue="company"
            >
              <p data-cy="company-landlord-choice">Jeg leier ut via en</p>
              <p class="font-weight-bold">virksomhet</p>
            </hl-RadioButtonTapWithIcon>
          </div>
        </div>

        <div class="row mt-4 pt-4 pb-5">
          <div class="col-12">
            <label class="form-group form-check text-left">
              <p class="ml-5">Jeg aksepterer <a href="https://www.husleie.no/juridisk/generelle-vilkar/" target="_blank">Husleie.no sine generelle vilkår</a></p>
              <input name="GDPR" v-model="signup.user.accepted.GDPR" v-validate="'required'" type="checkbox" data-cy="accept-general-terms">
              <span class="checkmark checkmark-sm"></span>
            </label>

            <div v-if="_hl_errors.has('GDPR')" class="error-message text-danger text-left mb-4">
                <span class="error-icon"></span>
                {{ _hl_errors.first('GDPR') }}
            </div>
          </div>


          <div class="col-12">
            <label class="form-group form-check text-left">
              <p class="ml-5">Jeg aksepterer <a href="https://www.husleie.no/juridisk/personvern/" target="_blank">Husleie.no sin personvernerklæring</a></p>
              <input name="terms" v-model="signup.user.accepted.terms" v-validate="'required'" type="checkbox" data-cy="accept-privacy-terms">
              <span class="checkmark checkmark-sm"></span>
            </label>

            <div v-if="_hl_errors.has('terms')" class="error-message text-danger text-left mb-4">
                <span class="error-icon"></span>
                {{ _hl_errors.first('terms') }}
            </div>
          </div>
        </div>

      <hl-NextBackBottomBar
        buttonSize="lg"
        nextLink="landlord"
        nextText="neste"
        :displayBack="false"
      />
    </div>

    <hl-Modal
      v-if="showEmailInUseModal"
      iconClass="budicon-email"
      title="Oops. E-posten er allerede i bruk"
    >
      <p>
        E-postadressen <b>{{ signup.user.email }}</b> er allerede
        registrert på en bruker av Husleie.no. Logg inn eller gå tilbake
        og registrer ny e-postadresse.
      </p>

      <div class="mt-4">
        <a :href="loginURL" class="btn btn-primary">
          Logg inn
        </a>
        <button
          class="btn btn-outline-secondary ml-3"
          @click="showEmailInUseModal = false"
        >
          Tilbake
        </button>
      </div>
    </hl-Modal>
  </div>
</template>

<script>
import Input from '@/components/Input.vue';
import RenderHTML from '@modules/design-system/components/atoms/RenderHTML.vue';
import Phone from '@modules/husleie-framework/src/components/forms/inputs/phone/Phone.vue';
import RadioButtonTapWithIcon from '@modules/husleie-framework/src/components/forms/inputs/radio-buttons/RadioButtonTapWithIcon.vue';
import H1 from '@modules/husleie-framework/src/components/headings/H1.vue';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import NextBackBottomBar from '@modules/husleie-framework/src/components/navigation/NextBackBottomBar.vue';
import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class.ts';
import i18nCommon from '@modules/i18n/mixins/Common';
import husleieapi from '@modules/husleie-api';
import hlValidateBeforeLeave from '@modules/husleie-framework/src/mixins/hlValidateBeforeLeave';
import {getExampleNumber, parsePhoneNumberFromString} from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';

const hlApi = new husleieapi();

export default {
  mixins: [i18nCommon, hlValidateBeforeLeave],
  components: {
    'hl-Modal': Modal,
    'hl-H1': H1,
    'hl-RadioButtonTapWithIcon': RadioButtonTapWithIcon,
    'hl-NextBackBottomBar': NextBackBottomBar,
    'hl-Input': Input,
    'hl-Phone': Phone,
    'a-RenderHTML': RenderHTML,
  },
  props: {
    signup: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showEmailInUseModal: false,
    }
  },
  computed: {
    phoneExample() {
      let prefix = parsePhoneNumberFromString(this.signup.user.phonePrefix+'444');
      if (prefix) {
        let exampleObject = getExampleNumber(prefix.country, examples);

        if (exampleObject)  {
          return exampleObject.formatNational();
        }
      }
    },
    loginURL() {
      return `${window.location.origin}/login`;
    },
  },

  methods: {
    checkEmailInUse() {
      hlApi.call(
        'UserApi.emailInUse',
        this.signup.user.email,
        (error, data) => {
          if (error) {
            LogOperations.error('API|emailInUse', error);
          } else {
            if (data.in_use) {
              this.signup.user.email = '';
              this.showEmailInUseModal = true;
            }
          }
        }
      );
    },
  },

  mounted() {
    /* GTM Tracking */
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event':'VirtualPageview',
      'virtualPageURL':'/signup/step1/userInformation',
      'virtualPageTitle': 'Signup | Step 1 | User Information'
    });
  }
};
</script>

<style lang="scss" scoped>
// custom checkbox
/* Hide the browser's default checkbox */
.form-check input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
  }

  /* Create a custom checkbox */
  .checkmark {
    position: absolute;
    background-color: #fff;
    border: 2px solid $gray-500;
    border-radius: $border-radius;
    transition: $transition-base;
  }

  .checkmark-lg {
    top: 0;
    left: 0;
    height: 25px;
    width: 25px;
  }

  .checkmark-sm {
    top: 3px;
    left: 0;
    height: 15px;
    width: 15px;
  }

  .form-check input:disabled ~ .checkmark {
      background: #fff !important;
      background-image: url('../../../modules/husleie-framework/icons/custom/dash.svg') !important;
      color: pink;
      background-size: 12px !important;
      background-position: center center !important;
      background-repeat: no-repeat !important;
      border: 2px solid $gray-400 !important;
  }

  /* On mouse-over, add a grey background color */
  .form-check:hover input ~ .checkmark {
    border: 2px solid $primary;
  }

  /* When the checkbox is checked, add a blue background */
  .form-check input:checked ~ .checkmark {
    background-color: $primary;
    border: 2px solid $primary;
    background-image: url('../../../modules/husleie-framework/icons/custom/checkmark.svg');
    background-size: 12px;
    background-position: center center;
    background-repeat: no-repeat;
  }


  .error-message {
      font-size: .8em;
      padding: .8rem 1rem;
      box-shadow: $box-shadow-2;
      border-radius: $border-radius;

      .error-icon {
        display:inline-block;
        position: relative;
        top: 3px;
        width: 16px;
        height: 16px;
        background: $danger;
        background-image: url('../../../modules/husleie-framework/icons/custom/cross.svg');
        background-size: 8px;
        background-position: center center;
        background-repeat: no-repeat;
        border-radius: 50%;
        color: #ffffff;
        font-size: .8em;
        margin-right: .8em;
        text-align: center;
      }
    }
// end of custom checkbox
</style>
